import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Users, 
  Shield,
  UserCog,
  Loader2,
  LogOut,
  ChevronRight,
  Wallet,
  BarChart3,
  FileSpreadsheet,
  Lock,
  Building2,
  KeyRound,
  Layers,
  PieChart,
  Info,
  ShoppingCart,
  Sparkles,
  ClipboardCheck
} from 'lucide-react';
import { cn } from '../lib/utils';

const ImpostazioniPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin, canManageUsers, canManageLocali, isSuperAdmin } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleBadge = (ruolo) => {
    const badges = {
      'admin': { icon: Shield, label: 'Admin', color: 'bg-red-100 text-red-800' },
      'manager': { icon: UserCog, label: 'Manager', color: 'bg-blue-100 text-blue-800' },
      'operatore_cassa': { icon: Users, label: 'Operatore', color: 'bg-green-100 text-green-800' },
      'analista': { icon: BarChart3, label: 'Analista', color: 'bg-purple-100 text-purple-800' },
    };
    const badge = badges[ruolo] || badges['operatore_cassa'];
    const Icon = badge.icon;
    
    return (
      <span className={cn("inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium", badge.color)}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const canAccessAnalytics = ['admin', 'manager', 'analista'].includes(user?.ruolo);
  const canAccessUscite = ['admin', 'manager'].includes(user?.ruolo);

  const menuItems = [
    // Super admin only: vista trasversale
    {
      label: 'Dashboard di Gruppo',
      description: 'Vista trasversale su tutti i locali',
      icon: Layers,
      path: '/dashboard-gruppo',
      visible: isSuperAdmin(),
      highlight: true
    },
    // Admin / Super admin
    {
      label: 'Gestione Locali',
      description: 'Crea, modifica e disattiva i locali',
      icon: Building2,
      path: '/locali',
      visible: canManageLocali(),
      highlight: true
    },
    // Admin / Super admin
    {
      label: 'Gestione Utenti',
      description: 'Utenti, ruoli e assegnazioni multi-locale',
      icon: Users,
      path: '/gestione-utenti',
      visible: canManageUsers(),
      highlight: true
    },
    // Tutti gli utenti autenticati
    {
      label: 'Cambia Password',
      description: 'Aggiorna la tua password personale',
      icon: KeyRound,
      path: '/cambia-password',
      visible: !!user,
    },
    // Admin & Manager
    {
      label: 'Uscite di Cassa',
      description: 'Registra e visualizza le spese',
      icon: Wallet,
      path: '/uscite',
      visible: canAccessUscite
    },
    // Analytics users
    {
      label: 'Dashboard Analytics',
      description: 'Statistiche e report',
      icon: BarChart3,
      path: '/analytics',
      visible: canAccessAnalytics
    },
    // Tutti gli utenti con accesso al locale
    {
      label: 'Controllo di Gestione',
      description: 'Venduto, coperti, riempimento e query sui prodotti',
      icon: PieChart,
      path: '/venduto',
      visible: true,
      highlight: true
    },
    // Tutti gli utenti con accesso al locale
    {
      label: 'Acquisti e Costi',
      description: 'Prezzario fornitori, fatture passive e storico prezzi',
      icon: ShoppingCart,
      path: '/acquisti',
      visible: true,
      highlight: true
    },
    // Tutti gli utenti con accesso al locale
    {
      label: 'Promemoria mensile',
      description: 'Come estrarre i dati dalle piattaforme e checklist di chiusura del mese',
      icon: ClipboardCheck,
      path: '/promemoria',
      visible: true,
      highlight: true
    },
    // Tutti gli utenti con accesso al locale
    {
      label: 'Assistente AI',
      description: 'Domande su venduto, acquisti, costi e personale',
      icon: Sparkles,
      path: '/assistente',
      visible: true,
      highlight: true
    },
    // Admin only
    {
      label: 'Import Dati',
      description: 'Importa chiusure da Excel',
      icon: FileSpreadsheet,
      path: '/import',
      visible: isAdmin()
    },
    // Tutti gli utenti autenticati
    {
      label: 'Info & Versioni',
      description: 'Versione in uso, data di aggiornamento e storico dei rilasci',
      icon: Info,
      path: '/info',
      visible: !!user,
    },
  ];

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold font-heading tracking-tight">Impostazioni</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-lg mx-auto space-y-6">
        {/* Current User Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Il tuo account</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">{user?.nome}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
              {getRoleBadge(user?.ruolo)}
            </div>
            <Button
              variant="outline"
              className="w-full mt-4 text-destructive hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </CardContent>
        </Card>

        {/* Menu Items */}
        <div className="space-y-2">
          {menuItems.filter(item => item.visible).map((item) => (
            <Card 
              key={item.label}
              className={cn(
                "cursor-pointer transition-all",
                item.disabled && "opacity-60",
                item.highlight && "border-primary/50 bg-primary/5",
                !item.disabled && "hover:border-primary/30 hover:bg-muted/50"
              )}
              onClick={() => !item.disabled && item.path && navigate(item.path)}
              data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center",
                      item.highlight ? "bg-primary/10" : "bg-muted"
                    )}>
                      <item.icon className={cn(
                        "w-5 h-5",
                        item.highlight ? "text-primary" : "text-muted-foreground"
                      )} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{item.label}</p>
                        {item.placeholder && (
                          <span className="text-xs bg-muted px-2 py-0.5 rounded">Coming soon</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                  {!item.disabled ? (
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <Lock className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Role Permissions Info */}
        <Card className="bg-secondary/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Permessi del tuo ruolo</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            {user?.ruolo === 'admin' && (
              <p>Hai accesso completo a tutte le funzionalità dell'app.</p>
            )}
            {user?.ruolo === 'manager' && (
              <p>Puoi gestire chiusure, uscite e visualizzare analytics. Non puoi gestire gli utenti.</p>
            )}
            {user?.ruolo === 'operatore_cassa' && (
              <p>Puoi inserire e modificare le chiusure degli ultimi 3 giorni.</p>
            )}
            {user?.ruolo === 'analista' && (
              <p>Puoi visualizzare dati e analytics in sola lettura.</p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ImpostazioniPage;
